'use client';

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { LatencyPoint } from '@/hooks/useDashboardRealtime';

interface Props {
  data: LatencyPoint[];
  loading: boolean;
}

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ dataKey: string; color: string; value: number }>; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-surface-overlay border border-border rounded-lg px-3 py-2 shadow-xl text-xs">
        <p className="text-muted mb-1 font-mono">{label}</p>
        {payload.map((p) => (
          <div key={`tip-lat-${p.dataKey}`} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-foreground-muted">{p.dataKey}:</span>
            <span className="font-semibold text-foreground font-mono">{p.value} ms</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function LatencyChart({ data, loading }: Props) {
  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-foreground">Latency Distribution</p>
          <p className="text-[11px] text-muted mt-0.5">
            Milliseconds · P50/P95/P99 · Last 3h
            {!loading && data.length > 0 && (
              <span className="inline-flex items-center gap-1 ml-2">
                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse inline-block" />
                <span className="text-success">Live</span>
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-success" />
            <span className="text-muted">P50</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-warning" />
            <span className="text-muted">P95</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-danger" />
            <span className="text-muted">P99</span>
          </div>
        </div>
      </div>
      {loading && data.length === 0 ? (
        <div className="h-[180px] bg-surface-raised rounded animate-pulse" />
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 14% 20%)" vertical={false} />
            <XAxis dataKey="time_label" tick={{ fontSize: 10, fill: 'hsl(222 10% 45%)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: 'hsl(222 10% 45%)' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={50} stroke="hsl(0 84% 60%)" strokeDasharray="4 4" strokeOpacity={0.5} />
            <Line type="monotone" dataKey="p50" stroke="hsl(142 71% 45%)" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="p95" stroke="hsl(38 92% 50%)" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="p99" stroke="hsl(0 84% 60%)" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}